import React, { useEffect, useState, useRef } from "react";
import { AutomationAction } from "../../../../typescript/interfaces/AutomationTypes";
import { EmailTemplate } from "../../../../typescript/interfaces/EmailType";
import { getEmailTemplates } from "../../../../utils/email/getEmailTemplates";
import { useUser } from "../../../routing/UserContext";
import TextEditor2 from "../../../text-editor/TextEditor2";
import InfoIcon from "@mui/icons-material/Info";
import SearchableDropDownWithId from "../../../widgets/SearchableDropDownWithId";

interface EmailAutomationTemplateProps {
  action: AutomationAction;
  updateAction: (updates: Partial<AutomationAction>) => void;
}

const EmailVariablesToolTip = () => (
  <div className="absolute right-0 w-72 p-3 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
    <div className="relative">
      <div className="absolute -top-2 right-2 w-3 h-3 bg-gray-800 transform rotate-45"></div>
      <p>
        Personaliza tu correo utilizando variables dinámicas:
        <br />• {"{{nombre}}"} - Nombre del candidato
        <br />• {"{{cargo}}"} - Nombre del cargo
        <br />• {"{{empresa}}"} - Nombre de la empresa
        <br />
        <span className="text-gray-300 text-xs mt-2">
          Ejemplo: "Hola {"{{ nombre }}"}, gracias por aplicar al puesto de{" "}
          {"{{ cargo }}"}"
        </span>
      </p>
    </div>
  </div>
);

const EmailTemplateInfoToolTip = () => (
  <div className="absolute right-0 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
    <div className="relative">
      <div className="absolute -top-2 right-2 w-3 h-3 bg-gray-800 transform rotate-45"></div>
      <p>
        Puedes crear y editar tus plantillas en Configuraciones.
        <br />
        <span className="text-gray-300 text-xs mt-1">
          Haz clic en tu foto de perfil y selecciona "Configuración" para
          acceder a la gestión de plantillas.
        </span>
      </p>
    </div>
  </div>
);

function EmailAutomationTemplate({
  action,
  updateAction,
}: EmailAutomationTemplateProps) {
  const { companyData } = useUser();
  const isFirstMount = useRef(true);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!companyData?._id) return;

      try {
        if (isFirstMount.current) {
          console.log("fetching templates");
          setIsLoading(true);
          setError(null);

          const response = await getEmailTemplates(companyData._id);
          setTemplates(response);

          // Only set the initial data on first mount
          if (response.length > 0 && !action.config.savedMessage) {
            const firstTemplate = response[0];
            updateAction({
              config: {
                ...action.config,
                emailTemplate: firstTemplate.id,
                savedMessage: {
                  name: firstTemplate.name,
                  subject: firstTemplate.subject,
                  content: firstTemplate.content,
                  companyId: firstTemplate.companyId,
                },
              },
            });
          }
          isFirstMount.current = false;
        }
      } catch (error) {
        setError(
          "Error al cargar las plantillas. Por favor, intente nuevamente."
        );
        console.error("Error fetching templates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [
    companyData?._id,
    action.config.savedMessage,
    action.config,
    updateAction,
  ]);

  const handleTemplateSelect = (selectedId: string) => {
    if (selectedId === "") {
      updateAction({
        config: {
          ...action.config,
          emailTemplate: "",
          savedMessage: {
            name: "",
            subject: action.config.savedMessage?.subject || "",
            content: action.config.savedMessage?.content || "",
            companyId: companyData?._id || "",
          },
        },
      });
      return;
    }

    const selected = templates.find((t) => t.id === selectedId);
    if (selected) {
      updateAction({
        config: {
          ...action.config,
          emailTemplate: selected.id,
          savedMessage: {
            name: selected.name,
            subject: selected.subject,
            content: selected.content,
            companyId: selected.companyId,
          },
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Destinatario
        </label>
        <select
          value={action.config.recipientType || ""}
          onChange={(e) =>
            updateAction({
              config: {
                ...action.config,
                recipientType: e.target.value as "APPLICANT" | "TEAM" | "BOTH",
              },
            })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="APPLICANT">Candidato</option>
          <option value="TEAM">Equipo</option>
          <option value="BOTH">Ambos</option>
        </select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Plantilla de Email (Opcional)
          </label>
          <div className="relative group">
            <InfoIcon className="text-gray-400 w-3.5 h-3.5" />
            <EmailTemplateInfoToolTip />
          </div>
        </div>
        {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
        <SearchableDropDownWithId
          options={
            !isLoading
              ? templates.map((template) => ({
                  name: template.name,
                  id: template.id || "",
                }))
              : []
          }
          placeholder={
            isLoading ? "Cargando plantillas..." : "Seleccionar plantilla..."
          }
          value={action.config.savedMessage?.name || ""}
          onChange={handleTemplateSelect}
        />
      </div>

      <div className="space-y-4 p-4 border border-gray-200 rounded-md">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Asunto <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={action.config.savedMessage?.subject || ""}
              onChange={(e) => {
                updateAction({
                  config: {
                    ...action.config,
                    savedMessage: {
                      ...(action.config.savedMessage || {
                        name: "",
                        subject: "",
                        content: "",
                        companyId: companyData?._id || "",
                      }),
                      subject: e.target.value,
                    },
                  },
                });
              }}
              className={`w-full px-3 py-2 border 
                   border-gray-300
        
               rounded-md`}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2 justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Contenido <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <InfoIcon className="text-gray-400 w-4 h-4" />
                <EmailVariablesToolTip />
              </div>
            </div>
            <TextEditor2
              editorName=""
              htmlValue={action.config.savedMessage?.content || ""}
              setHTMLValue={(html) => {
                updateAction({
                  config: {
                    ...action.config,
                    savedMessage: {
                      ...(action.config.savedMessage || {
                        name: "",
                        subject: "",
                        content: "",
                        companyId: companyData?._id || "",
                      }),
                      content: html,
                    },
                  },
                });
              }}
              noMarginTop={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailAutomationTemplate;
